import { useEffect, useMemo, useState, useCallback, useRef, lazy, Suspense } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  ReactFlow,
  Background,
  Controls,
  MiniMap,
  Position,
  Handle,
  type Node,
  type Edge,
  type NodeMouseHandler,
  type NodeProps,
} from '@xyflow/react';
import dagre from 'dagre';
import { toPng } from 'html-to-image';
import { useTheme } from '../lib/theme';
import { Skeleton } from '../components/ui/Skeleton';
import { Search, RefreshCw, Eye, EyeOff, Download } from 'lucide-react';
import { useApi } from '../contexts/ApiContext';
import { ApiResponse } from '../types';
import { LivenessDot } from '../components/LivenessBadge';
import { logoUrlFor } from '../lib/registrar-logos';
import { keywordMatches } from '../lib/keywordMatch';
import '@xyflow/react/dist/style.css';

// Sentinel used as the "email" key for WHOIS-redacted contacts. All non-email
// values (privacy proxies, ICANN redaction strings, blanks) collapse into
// this single bucket so the graph shows one muted node instead of dozens of
// long redaction-text nodes.
const NO_EMAIL_KEY = '__no_public_whois_email__';

// Heuristic: is this string a real, useful email? Filters out empties,
// common privacy-redaction phrases, and anything that doesn't even look
// like an email address.
const REDACTION_TOKENS = [
  'redacted',
  'privacy',
  'please query',
  'rdds service',
  'data protected',
  'not disclosed',
  'whois agent',
  'whoisguard',
  'domains by proxy',
  'withheldforprivacy',
  'gdpr',
  'masked',
];
function isRealEmail(value: string | null | undefined): boolean {
  if (!value) return false;
  const v = value.trim().toLowerCase();
  if (!v) return false;
  // Must look like an email: contain @, a dot, no spaces, sane length
  if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v)) return false;
  if (v.length > 254) return false;
  for (const tok of REDACTION_TOKENS) {
    if (v.includes(tok)) return false;
  }
  return true;
}

// 12-color qualitative palette. Each registrar gets a stable color by hashing
// its id, so the same registrar keeps the same hue across reloads.
const REGISTRAR_PALETTE: { fill: string; border: string; text: string; edge: string }[] = [
  { fill: '#dbeafe', border: '#2563eb', text: '#1e3a8a', edge: '#60a5fa' }, // blue
  { fill: '#fce7f3', border: '#db2777', text: '#831843', edge: '#f472b6' }, // pink
  { fill: '#dcfce7', border: '#16a34a', text: '#14532d', edge: '#4ade80' }, // green
  { fill: '#fed7aa', border: '#ea580c', text: '#7c2d12', edge: '#fb923c' }, // orange
  { fill: '#e9d5ff', border: '#9333ea', text: '#581c87', edge: '#c084fc' }, // purple
  { fill: '#fef3c7', border: '#ca8a04', text: '#713f12', edge: '#facc15' }, // amber
  { fill: '#ccfbf1', border: '#0d9488', text: '#134e4a', edge: '#2dd4bf' }, // teal
  { fill: '#fee2e2', border: '#dc2626', text: '#7f1d1d', edge: '#f87171' }, // red
  { fill: '#cffafe', border: '#0891b2', text: '#164e63', edge: '#22d3ee' }, // cyan
  { fill: '#ede9fe', border: '#7c3aed', text: '#4c1d95', edge: '#a78bfa' }, // violet
  { fill: '#d1fae5', border: '#059669', text: '#064e3b', edge: '#34d399' }, // emerald
  { fill: '#fae8ff', border: '#a21caf', text: '#701a75', edge: '#e879f9' }, // fuchsia
];
function colorFor(registrarType: string): typeof REGISTRAR_PALETTE[number] {
  let hash = 0;
  for (let i = 0; i < registrarType.length; i++) {
    hash = (hash * 31 + registrarType.charCodeAt(i)) | 0;
  }
  return REGISTRAR_PALETTE[Math.abs(hash) % REGISTRAR_PALETTE.length];
}


interface MindMapRow {
  domain_id: string;
  domain_name: string;
  tld: string | null;
  registrar_id: string;       // either a registrar_accounts.id or a registrars.id (e.g. 'manual')
  account_label: string;       // ra.label (or registrar name fallback for non-account rows)
  registrar_type: string;      // canonical type id ('godaddy', 'manual', etc) — used for color + grouping
  registrar_name: string;      // official registrar display name from the registrars table
  liveness_status: string | null;  // latest liveness probe ('live'|'parked'|... ) or null
  expiry_date: string | null;      // ISO date; drives the expiry-urgency dot
  registrant_email: string | null;
  registrant_name: string | null;
  registrant_organization: string | null;
  registrant_phone: string | null;
  registrant_country: string | null;
  registrant_state: string | null;
  registrant_city: string | null;
  admin_email: string | null;
  admin_name: string | null;
  admin_organization: string | null;
  admin_phone: string | null;
  tech_email: string | null;
  tech_name: string | null;
  tech_organization: string | null;
  tech_phone: string | null;
}

// Aggregated contact details per email key. We collect the first non-null
// value seen across all rows that share this email (or any role-specific
// detail when it's the email's role for that row).
interface ContactDetails {
  name: string | null;
  organization: string | null;
  phone: string | null;
  country: string | null;
  state: string | null;
  city: string | null;
}

type Role = 'registrant' | 'admin' | 'tech';

const NODE_WIDTH = 260;
const NODE_HEIGHT = 50;
// Per-kind heights for dagre layout. Long emails wrap into 2-3 lines on
// account-tier nodes; using a single height made sibling nodes overlap.
// Email tier renders multi-line registrant details so it gets the most.
// Registrar tier needs room for the "Name · N accounts · M domains" line
// to wrap on narrow nodes.
const NODE_HEIGHT_BY_KIND: Record<string, number> = {
  email: 140, // email + name + org + phone/location + count line
  registrar: 110, // name line + TLD chips row (can be 1–2 lines of chips)
  account: 76, // wraps when account label is a long email
  domain: 48,
};

const EXPOSED_STYLE = {
  background: '#fff1f2',
  border: '2px solid #dc2626',
  borderLeft: '4px solid #dc2626',
  color: '#7f1d1d',
} as const;

function isEmailExposed(email: string, details: ContactDetails, keywords: string[]): boolean {
  if (keywords.length === 0) return false;
  const fields = [
    email,
    details.name,
    details.organization,
    details.phone,
    details.city,
    details.state,
    details.country,
  ].filter(Boolean) as string[];
  // Whole words only, like the server: "ian" must not match "PrivacyGuardian".
  return keywords.some(kw => fields.some(f => keywordMatches(f, kw)));
}

function buildGraph(
  rows: MindMapRow[],
  opts: {
    hideAdminTech: boolean;
    emailFilter: string;
    registrarFilter: Set<string>;
    expandedAccounts: Set<string>;
    hideRedacted: boolean;
    focusedEmail: string | null;
    exposedDomainIds: Set<string>;
    exposureKeywords: string[];
  }
): { nodes: Node[]; edges: Edge[]; emailCount: number; redactedCount: number } {
  const filterText = opts.emailFilter.trim().toLowerCase();

  // 3-tier tree:
  //   email (+ aggregated contact details)
  //   └── registrar_type (with display name)
  //       └── account_id (= row.registrar_id, with account label)
  //           └── domain_id (with name + roles)
  type AccountBucket = { account_label: string; domains: Map<string, { name: string; tld: string | null; roles: Set<Role>; liveness_status: string | null; expiry_date: string | null }> };
  type RegistrarBucket = { registrar_name: string; accounts: Map<string, AccountBucket> };
  type EmailBucket = { details: ContactDetails; registrars: Map<string, RegistrarBucket> };
  const tree = new Map<string, EmailBucket>();

  let redactedDomainsTotal = 0;
  const redactedDomainSeen = new Set<string>();

  // Pull out role-specific contact fields from a row. registrant has the most
  // detail; admin/tech only carry name/org/phone in the schema.
  const detailsFor = (role: Role, row: MindMapRow): ContactDetails => {
    if (role === 'registrant') {
      return {
        name: row.registrant_name,
        organization: row.registrant_organization,
        phone: row.registrant_phone,
        country: row.registrant_country,
        state: row.registrant_state,
        city: row.registrant_city,
      };
    }
    if (role === 'admin') {
      return {
        name: row.admin_name,
        organization: row.admin_organization,
        phone: row.admin_phone,
        country: null, state: null, city: null,
      };
    }
    return {
      name: row.tech_name,
      organization: row.tech_organization,
      phone: row.tech_phone,
      country: null, state: null, city: null,
    };
  };

  // Merge role-specific details into the email's accumulator. First-non-null
  // wins per field; for the same email, this picks whichever row had data.
  const mergeDetails = (acc: ContactDetails, incoming: ContactDetails) => {
    if (!acc.name && incoming.name) acc.name = incoming.name;
    if (!acc.organization && incoming.organization) acc.organization = incoming.organization;
    if (!acc.phone && incoming.phone) acc.phone = incoming.phone;
    if (!acc.country && incoming.country) acc.country = incoming.country;
    if (!acc.state && incoming.state) acc.state = incoming.state;
    if (!acc.city && incoming.city) acc.city = incoming.city;
  };

  const addEntry = (email: string | null, role: Role, row: MindMapRow) => {
    if (opts.hideAdminTech && role !== 'registrant') return;
    if (opts.registrarFilter.size > 0 && !opts.registrarFilter.has(row.registrar_type)) return;

    const real = isRealEmail(email);
    const key = real ? email!.trim().toLowerCase() : NO_EMAIL_KEY;

    if (!real) {
      if (opts.hideRedacted) return;
      if (!redactedDomainSeen.has(row.domain_id)) {
        redactedDomainSeen.add(row.domain_id);
        redactedDomainsTotal++;
      }
    }

    if (filterText && !key.includes(filterText) && key !== NO_EMAIL_KEY) return;
    if (opts.focusedEmail && opts.focusedEmail !== key) return;

    let bucket = tree.get(key);
    if (!bucket) {
      bucket = {
        details: { name: null, organization: null, phone: null, country: null, state: null, city: null },
        registrars: new Map(),
      };
      tree.set(key, bucket);
    }
    if (real) mergeDetails(bucket.details, detailsFor(role, row));

    let reg = bucket.registrars.get(row.registrar_type);
    if (!reg) {
      reg = { registrar_name: row.registrar_name, accounts: new Map() };
      bucket.registrars.set(row.registrar_type, reg);
    }
    let acc = reg.accounts.get(row.registrar_id);
    if (!acc) {
      acc = { account_label: row.account_label, domains: new Map() };
      reg.accounts.set(row.registrar_id, acc);
    }
    let dom = acc.domains.get(row.domain_id);
    if (!dom) {
      dom = { name: row.domain_name, tld: row.tld, roles: new Set(), liveness_status: row.liveness_status, expiry_date: row.expiry_date };
      acc.domains.set(row.domain_id, dom);
    }
    dom.roles.add(role);
  };

  for (const row of rows) {
    addEntry(row.registrant_email, 'registrant', row);
    addEntry(row.admin_email, 'admin', row);
    addEntry(row.tech_email, 'tech', row);
  }

  const nodes: Node[] = [];
  const edges: Edge[] = [];

  for (const [email, bucket] of tree) {
    const emailId = `email:${email}`;
    const isRedacted = email === NO_EMAIL_KEY;
    const regMap = bucket.registrars;
    let totalDomains = 0;
    let totalAccounts = 0;
    for (const reg of regMap.values()) {
      totalAccounts += reg.accounts.size;
      for (const acc of reg.accounts.values()) totalDomains += acc.domains.size;
    }

    // Build the rich email-tier label: email + registrant name/org/phone/
    // country if any of those are populated. For the redacted bucket we keep
    // the simple lock label.
    const d = bucket.details;
    const locationParts = [d.city, d.state, d.country].filter(Boolean);
    const locationLine = locationParts.length > 0 ? locationParts.join(', ') : null;
    const hasAnyDetail = d.name || d.organization || d.phone || locationLine;

    const emailExposed = !isRedacted && isEmailExposed(email, d, opts.exposureKeywords);

    const emailLabel = isRedacted ? (
      `🔒 No public WHOIS email  ·  ${totalDomains} domain${totalDomains === 1 ? '' : 's'}`
    ) : (
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 2, width: '100%', textAlign: 'left' }}>
        <div style={{ fontWeight: 700, fontSize: 13, color: emailExposed ? '#991b1b' : '#4c1d95', wordBreak: 'break-all', lineHeight: 1.2 }}>
          {emailExposed && <span style={{ marginRight: 4 }}>⚠️</span>}{email}
        </div>
        {d.name && (
          <div style={{ fontSize: 11, color: '#581c87', fontWeight: 600 }}>
            {d.name}
          </div>
        )}
        {d.organization && d.organization !== d.name && (
          <div style={{ fontSize: 11, color: '#6d28d9', fontStyle: 'italic' }}>
            {d.organization}
          </div>
        )}
        {(d.phone || locationLine) && (
          <div style={{ fontSize: 10, color: '#7c3aed', display: 'flex', flexWrap: 'wrap', gap: '0 6px' }}>
            {d.phone && <span>📞 {d.phone}</span>}
            {locationLine && <span>📍 {locationLine}</span>}
          </div>
        )}
        <div style={{ fontSize: 10, color: '#a78bfa', marginTop: 1, fontWeight: 500 }}>
          {totalDomains} domain{totalDomains === 1 ? '' : 's'}
          {totalAccounts > 1 && ` · ${totalAccounts} accounts`}
          {!hasAnyDetail && ' · no other contact details'}
        </div>
      </div>
    );

    nodes.push({
      id: emailId,
      type: 'default',
      data: { label: emailLabel, kind: 'email', is_redacted: isRedacted, is_exposed: emailExposed, total_domains: totalDomains, email_key: email },
      position: { x: 0, y: 0 },
      style: isRedacted
        ? {
            background: '#f1f5f9',
            border: '1px dashed #94a3b8',
            color: '#475569',
            fontWeight: 500,
            fontStyle: 'italic',
            borderRadius: 12,
            padding: '8px 14px',
            width: NODE_WIDTH,
            cursor: 'pointer',
          }
        : emailExposed
        ? {
            background: 'linear-gradient(135deg, #fff1f2 0%, #fee2e2 100%)',
            border: '2px solid #dc2626',
            color: '#7f1d1d',
            fontWeight: 700,
            borderRadius: 12,
            padding: '10px 14px',
            width: NODE_WIDTH,
            cursor: 'pointer',
            boxShadow: '0 2px 8px rgba(220, 38, 38, 0.25)',
            height: 'auto',
          }
        : {
            background: 'linear-gradient(135deg, #ede9fe 0%, #ddd6fe 100%)',
            border: '2px solid #7c3aed',
            color: '#4c1d95',
            fontWeight: 700,
            borderRadius: 12,
            padding: '10px 14px',
            width: NODE_WIDTH,
            cursor: 'pointer',
            boxShadow: '0 2px 6px rgba(124, 58, 237, 0.18)',
            height: 'auto',
          },
      sourcePosition: Position.Right,
      targetPosition: Position.Left,
    });

    for (const [registrarType, reg] of regMap) {
      const regNodeId = `${emailId}|regtype:${registrarType}`;
      const palette = colorFor(registrarType);
      const regDomainCount = Array.from(reg.accounts.values()).reduce((s, a) => s + a.domains.size, 0);
      // Count TLDs across all domains under this registrar node
      const tldCounts = new Map<string, number>();
      for (const acc of reg.accounts.values()) {
        for (const dom of acc.domains.values()) {
          const t = dom.tld ? dom.tld.replace(/^\./, '') : dom.name.split('.').pop() || '?';
          tldCounts.set(t, (tldCounts.get(t) || 0) + 1);
        }
      }
      const tldChips = Array.from(tldCounts.entries())
        .sort((a, b) => b[1] - a[1]);

      const logoUrl = logoUrlFor(registrarType);
      const summary = reg.accounts.size > 1
        ? `${reg.accounts.size} accounts  ·  ${regDomainCount} domains`
        : `${regDomainCount} domain${regDomainCount === 1 ? '' : 's'}`;
      const palette2 = colorFor(registrarType);
      const regLabel = (
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 5, width: '100%' }}>
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8, justifyContent: 'center' }}>
            {logoUrl && (
              <img
                src={logoUrl}
                alt=""
                width={18}
                height={18}
                style={{ borderRadius: 3, flexShrink: 0 }}
                onError={(e) => { (e.currentTarget as HTMLImageElement).style.display = 'none'; }}
              />
            )}
            <span>{reg.registrar_name}  ·  {summary}</span>
          </span>
          {tldChips.length > 0 && (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '3px 4px', justifyContent: 'center' }}>
              {tldChips.map(([tld, count]) => (
                <span
                  key={tld}
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 3,
                    padding: '1px 6px',
                    borderRadius: 999,
                    fontSize: 10,
                    fontWeight: 600,
                    background: palette2.fill,
                    border: `1px solid ${palette2.border}`,
                    color: palette2.text,
                    whiteSpace: 'nowrap',
                  }}
                >
                  .{tld}
                  {count > 1 && (
                    <span style={{
                      background: palette2.border,
                      color: '#fff',
                      borderRadius: 999,
                      padding: '0 4px',
                      fontSize: 9,
                      fontWeight: 700,
                    }}>
                      {count}
                    </span>
                  )}
                </span>
              ))}
            </div>
          )}
        </div>
      );
      nodes.push({
        id: regNodeId,
        type: 'default',
        data: {
          label: regLabel,
          kind: 'registrar',
          registrar_type: registrarType,
        },
        position: { x: 0, y: 0 },
        style: {
          background: palette.fill,
          border: `3px solid ${palette.border}`,
          color: palette.text,
          fontWeight: 700,
          padding: '8px 14px',
          width: NODE_WIDTH,
          letterSpacing: '0.01em',
        },
        sourcePosition: Position.Right,
        targetPosition: Position.Left,
      });
      edges.push({
        id: `e:${emailId}->${regNodeId}`,
        source: emailId,
        target: regNodeId,
        type: 'straight',
        animated: !isRedacted && !emailExposed,
        style: {
          stroke: isRedacted ? '#cbd5e1' : emailExposed ? '#dc2626' : palette.edge,
          strokeWidth: emailExposed ? 2.5 : 2,
          strokeDasharray: isRedacted ? '4 4' : undefined,
        },
      });

      // Account tier — clickable to expand/collapse domains underneath.
      for (const [accountId, acc] of reg.accounts) {
        const accNodeId = `${regNodeId}|acc:${accountId}`;
        const isExpanded = opts.expandedAccounts.has(accNodeId);
        const chevron = isExpanded ? '▼' : '▶';
        nodes.push({
          id: accNodeId,
          type: 'default',
          data: {
            label: `${chevron}  ${acc.account_label}  ·  ${acc.domains.size}`,
            kind: 'account',
            registrar_type: registrarType,
            account_id: accountId,
          },
          position: { x: 0, y: 0 },
          style: {
            background: '#ffffff',
            border: `1.5px solid ${palette.border}`,
            borderLeft: `4px solid ${palette.border}`,
            color: palette.text,
            fontWeight: 500,
            fontSize: 13,
            padding: '6px 12px',
            width: NODE_WIDTH,
            cursor: 'pointer',
            boxShadow: isExpanded ? `0 1px 4px ${palette.border}33` : 'none',
          },
          sourcePosition: Position.Right,
          targetPosition: Position.Left,
        });
        edges.push({
          id: `e:${regNodeId}->${accNodeId}`,
          source: regNodeId,
          target: accNodeId,
          type: 'straight',
          style: { stroke: palette.edge, strokeWidth: 1.5 },
        });

        if (!isExpanded) continue;

        for (const [domainId, dom] of acc.domains) {
          const domNodeId = `${accNodeId}|dom:${domainId}`;
          const rolesLabel = Array.from(dom.roles)
            .map((r) => r[0].toUpperCase())
            .join('');
          const isExposed = opts.exposedDomainIds.has(domainId);
          nodes.push({
            id: domNodeId,
            type: 'domainNode',
            data: {
              label: `${dom.name}  ·  ${rolesLabel}`,
              name: dom.name,
              roles_label: rolesLabel,
              kind: 'domain',
              domain_id: domainId,
              registrar_type: registrarType,
              is_exposed: isExposed,
              liveness_status: dom.liveness_status,
              expiry_date: dom.expiry_date,
            },
            position: { x: 0, y: 0 },
            style: isExposed ? {
              ...EXPOSED_STYLE,
              padding: '5px 10px',
              width: NODE_WIDTH,
              fontSize: 11,
              cursor: 'pointer',
            } : {
              background: '#fafafa',
              border: `1px solid ${palette.border}66`,
              borderLeft: `3px solid ${palette.border}`,
              color: palette.text,
              padding: '5px 10px',
              width: NODE_WIDTH,
              fontSize: 11,
              cursor: 'pointer',
            },
            sourcePosition: Position.Right,
            targetPosition: Position.Left,
          });
          edges.push({
            id: `e:${accNodeId}->${domNodeId}`,
            source: accNodeId,
            target: domNodeId,
            type: 'straight',
            style: isExposed
              ? { stroke: '#dc2626', strokeWidth: 1.5, opacity: 0.8 }
              : { stroke: palette.edge, strokeWidth: 1, opacity: 0.6 },
          });
        }
      }
    }

    void totalDomains;
    void totalAccounts;
  }

  // Dagre layout
  const g = new dagre.graphlib.Graph();
  g.setDefaultEdgeLabel(() => ({}));
  g.setGraph({ rankdir: 'LR', nodesep: 24, ranksep: 90 });
  for (const n of nodes) {
    const kind = (n.data as { kind?: string }).kind || 'default';
    const h = NODE_HEIGHT_BY_KIND[kind] ?? NODE_HEIGHT;
    g.setNode(n.id, { width: NODE_WIDTH, height: h });
  }
  for (const e of edges) g.setEdge(e.source, e.target);
  dagre.layout(g);

  for (const n of nodes) {
    const pos = g.node(n.id);
    if (pos) {
      const kind = (n.data as { kind?: string }).kind || 'default';
      const h = NODE_HEIGHT_BY_KIND[kind] ?? NODE_HEIGHT;
      n.position = { x: pos.x - NODE_WIDTH / 2, y: pos.y - h / 2 };
    }
  }

  // Cluster compaction. Dagre's barycentric ranker leaves big gaps between
  // email subtrees of different sizes (one email with 100 domains expanded
  // and another with 1 produces ~600px of empty whitespace between them).
  // We group nodes by their email subtree, compute each cluster's bbox,
  // then re-stack them vertically with a fixed gap. This also doubles as
  // the "park redacted at the bottom" mechanism — the redacted cluster
  // sorts last with a wider top gap.
  const redactedEmailId = `email:${NO_EMAIL_KEY}`;
  const heightOf = (n: Node): number => {
    const kind = (n.data as { kind?: string }).kind || 'default';
    return NODE_HEIGHT_BY_KIND[kind] ?? NODE_HEIGHT;
  };

  type Cluster = { emailId: string; nodes: Node[]; top: number; bottom: number };
  const clusterMap = new Map<string, Node[]>();
  for (const n of nodes) {
    const m = n.id.match(/^email:[^|]+/);
    const key = m ? m[0] : 'orphan';
    let arr = clusterMap.get(key);
    if (!arr) { arr = []; clusterMap.set(key, arr); }
    arr.push(n);
  }
  const clusters: Cluster[] = [];
  for (const [emailId, ns] of clusterMap) {
    let top = Infinity, bottom = -Infinity;
    for (const n of ns) {
      top = Math.min(top, n.position.y);
      bottom = Math.max(bottom, n.position.y + heightOf(n));
    }
    clusters.push({ emailId, nodes: ns, top, bottom });
  }
  // Sort: redacted cluster always last; others by current Y.
  clusters.sort((a, b) => {
    const aR = a.emailId === redactedEmailId;
    const bR = b.emailId === redactedEmailId;
    if (aR !== bR) return aR ? 1 : -1;
    return a.top - b.top;
  });
  const CLUSTER_GAP = 60;        // standard gap between consecutive email subtrees
  const REDACTED_GAP = 120;      // extra space to visually separate the redacted bucket
  let cursorY = clusters.length > 0 ? clusters[0].top : 0;
  for (let i = 0; i < clusters.length; i++) {
    const c = clusters[i];
    const isRedacted = c.emailId === redactedEmailId;
    const targetTop = i === 0 ? cursorY : cursorY + (isRedacted ? REDACTED_GAP : CLUSTER_GAP);
    const delta = targetTop - c.top;
    if (Math.abs(delta) > 0.5) {
      for (const n of c.nodes) {
        n.position = { x: n.position.x, y: n.position.y + delta };
      }
      c.top += delta;
      c.bottom += delta;
    }
    cursorY = c.bottom;
  }

  return { nodes, edges, emailCount: tree.size, redactedCount: redactedDomainsTotal };
}

// Days until an ISO date (negative = already past). null if unparseable.
function daysUntil(iso: string | null): number | null {
  if (!iso) return null;
  const t = new Date(iso).getTime();
  if (isNaN(t)) return null;
  return Math.floor((t - Date.now()) / 86_400_000);
}

// Expiry urgency dot: red when expired / <30d, amber <90d, nothing beyond.
function ExpiryDot({ expiry }: { expiry: string | null }) {
  const d = daysUntil(expiry);
  if (d === null) return null;
  let color: string;
  let label: string;
  if (d < 0) { color = '#dc2626'; label = `Expired ${-d}d ago`; }
  else if (d < 30) { color = '#dc2626'; label = `Expires in ${d}d`; }
  else if (d < 90) { color = '#d97706'; label = `Expires in ${d}d`; }
  else return null;
  return <span title={label} style={{ display: 'inline-block', width: 7, height: 7, borderRadius: 9999, background: color, flexShrink: 0 }} />;
}

// Custom domain node: domain name + role label, plus a liveness dot and an
// expiry-urgency dot. Box styling (background/border/width) comes from the
// node's `style`; this renders the inner content + connection handles.
function DomainNode({ data }: NodeProps) {
  const d = data as unknown as {
    name: string; roles_label: string; is_exposed?: boolean;
    liveness_status: string | null; expiry_date: string | null;
  };
  return (
    <>
      <Handle type="target" position={Position.Left} style={{ opacity: 0 }} isConnectable={false} />
      <div style={{ display: 'flex', alignItems: 'center', gap: 5, width: '100%', minWidth: 0 }}>
        {d.is_exposed && <span style={{ flexShrink: 0 }}>⚠️</span>}
        {d.liveness_status && <LivenessDot status={d.liveness_status} />}
        <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {d.name}<span style={{ opacity: 0.5 }}>{'  ·  '}{d.roles_label}</span>
        </span>
        <ExpiryDot expiry={d.expiry_date} />
      </div>
      <Handle type="source" position={Position.Right} style={{ opacity: 0 }} isConnectable={false} />
    </>
  );
}

/** The original contact-email network graph ("Network" tab). */
function NetworkView({ tabs }: { tabs: React.ReactNode }) {
  const [, isDark] = useTheme();
  const { fetchApi } = useApi();
  const navigate = useNavigate();
  const flowWrapperRef = useRef<HTMLDivElement>(null);
  const [exporting, setExporting] = useState(false);
  const nodeTypes = useMemo(() => ({ domainNode: DomainNode }), []);
  const [rows, setRows] = useState<MindMapRow[]>([]);
  const [exposedDomainIds, setExposedDomainIds] = useState<Set<string>>(new Set());
  const [exposureKeywords, setExposureKeywords] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [emailFilter, setEmailFilter] = useState('');
  const [hideAdminTech, setHideAdminTech] = useState(false);
  const [hideRedacted, setHideRedacted] = useState(false);
  const [registrarFilter, setRegistrarFilter] = useState<Set<string>>(new Set());
  const [expandedAccounts, setExpandedAccounts] = useState<Set<string>>(new Set());
  const [hoveredEmail, setHoveredEmail] = useState<string | null>(null);
  const [focusedEmail, setFocusedEmail] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetchApi<ApiResponse<{ rows: MindMapRow[]; exposed_domain_ids: string[]; exposure_keywords: string[] }>>('/reports/mind-map');
      setRows(res.data?.rows || []);
      setExposedDomainIds(new Set(res.data?.exposed_domain_ids || []));
      setExposureKeywords(res.data?.exposure_keywords || []);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load mind map');
    } finally {
      setLoading(false);
    }
  }, [fetchApi]);

  useEffect(() => {
    load();
  }, [load]);

  // Distinct registrar TYPES for the chip filter (so toggling "GoDaddy" hides
  // every GoDaddy account at once instead of one chip per account).
  const registrars = useMemo(() => {
    const map = new Map<string, string>();
    for (const r of rows) map.set(r.registrar_type, r.registrar_name);
    return Array.from(map.entries())
      .map(([id, name]) => ({ id, name }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [rows]);

  // Portfolio stats for the overlay. rows is one row per domain (domain_whois
  // is 1:1 with domains), so counts need no de-duplication.
  const stats = useMemo(() => {
    const total = rows.length;
    const liveness = { live: 0, parked: 0, for_sale: 0, redirect: 0, offline: 0, error: 0 } as Record<string, number>;
    let exposed = 0;
    let expiringSoon = 0; // < 30 days
    const byRegistrar = new Map<string, number>();
    for (const r of rows) {
      if (r.liveness_status && r.liveness_status in liveness) liveness[r.liveness_status]++;
      if (exposedDomainIds.has(r.domain_id)) exposed++;
      const d = daysUntil(r.expiry_date);
      if (d !== null && d < 30) expiringSoon++;
      byRegistrar.set(r.registrar_name, (byRegistrar.get(r.registrar_name) ?? 0) + 1);
    }
    const topRegistrar = [...byRegistrar.entries()].sort((a, b) => b[1] - a[1])[0] ?? null;
    return { total, exposed, expiringSoon, liveness, topRegistrar, registrarCount: byRegistrar.size };
  }, [rows, exposedDomainIds]);

  const exportPng = useCallback(async () => {
    const el = flowWrapperRef.current?.querySelector('.react-flow') as HTMLElement | null;
    if (!el) return;
    setExporting(true);
    try {
      const dataUrl = await toPng(el, {
        backgroundColor: isDark ? '#0b1220' : '#f9fafb',
        pixelRatio: 2,
        filter: (node) => {
          // Skip the controls/minimap chrome so the export is just the graph.
          const cl = (node as HTMLElement).classList;
          return !cl || !(cl.contains('react-flow__controls') || cl.contains('react-flow__minimap'));
        },
      });
      const link = document.createElement('a');
      link.download = `mind-map-${new Date().toISOString().slice(0, 10)}.png`;
      link.href = dataUrl;
      link.click();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Export failed');
    } finally {
      setExporting(false);
    }
  }, []);

  const { nodes: rawNodes, edges: rawEdges, emailCount, redactedCount } = useMemo(
    () => buildGraph(rows, { hideAdminTech, emailFilter, registrarFilter, expandedAccounts, hideRedacted, focusedEmail, exposedDomainIds, exposureKeywords }),
    [rows, hideAdminTech, emailFilter, registrarFilter, expandedAccounts, hideRedacted, focusedEmail, exposedDomainIds, exposureKeywords]
  );

  // Hover dim: when hovering an email node, highlight only its subtree.
  // Built per render so it reflects the current graph and hover state.
  const { nodes, edges } = useMemo(() => {
    if (!hoveredEmail) return { nodes: rawNodes, edges: rawEdges };
    const keepIds = new Set<string>();
    keepIds.add(hoveredEmail);
    for (const e of rawEdges) {
      if (e.source === hoveredEmail) {
        keepIds.add(e.target);
      }
    }
    // Walk one more level: registrar → domain
    for (const e of rawEdges) {
      if (keepIds.has(e.source)) keepIds.add(e.target);
    }
    return {
      nodes: rawNodes.map(n => ({
        ...n,
        style: { ...n.style, opacity: keepIds.has(n.id) ? 1 : 0.18 },
      })),
      edges: rawEdges.map(e => ({
        ...e,
        style: {
          ...e.style,
          opacity: keepIds.has(e.source) && keepIds.has(e.target) ? 1 : 0.1,
        },
      })),
    };
  }, [rawNodes, rawEdges, hoveredEmail]);

  const onNodeClick: NodeMouseHandler = useCallback(
    (_evt, node) => {
      const data = node.data as { kind?: string; domain_id?: string };
      if (data.kind === 'domain' && data.domain_id) {
        navigate(`/domains/${data.domain_id}`);
      } else if (data.kind === 'account') {
        // Account tier expands/collapses its domain children.
        setExpandedAccounts((prev) => {
          const next = new Set(prev);
          if (next.has(node.id)) next.delete(node.id);
          else next.add(node.id);
          return next;
        });
      } else if (data.kind === 'email') {
        setFocusedEmail(prev => (prev === node.id.replace(/^email:/, '') ? null : node.id.replace(/^email:/, '')));
      }
      // 'registrar' (type tier) is informational — no click action.
    },
    [navigate]
  );

  const onNodeMouseEnter: NodeMouseHandler = useCallback((_evt, node) => {
    if ((node.data as { kind?: string }).kind === 'email') {
      setHoveredEmail(node.id);
    }
  }, []);

  const onNodeMouseLeave: NodeMouseHandler = useCallback(() => {
    setHoveredEmail(null);
  }, []);

  const expandAll = useCallback(() => {
    // Build every possible account-node id from the rows that currently pass
    // the filters, so we expand domains under all visible accounts.
    const ids = new Set<string>();
    const filterText = emailFilter.trim().toLowerCase();
    for (const row of rows) {
      const emails: Array<[string | null, Role]> = [
        [row.registrant_email, 'registrant'],
        [row.admin_email, 'admin'],
        [row.tech_email, 'tech'],
      ];
      for (const [rawEmail, role] of emails) {
        if (hideAdminTech && role !== 'registrant') continue;
        if (registrarFilter.size > 0 && !registrarFilter.has(row.registrar_type)) continue;
        const real = isRealEmail(rawEmail);
        const key = real ? rawEmail!.trim().toLowerCase() : NO_EMAIL_KEY;
        if (!real && hideRedacted) continue;
        if (focusedEmail && focusedEmail !== key) continue;
        if (filterText && key !== NO_EMAIL_KEY && !key.includes(filterText)) continue;
        ids.add(`email:${key}|regtype:${row.registrar_type}|acc:${row.registrar_id}`);
      }
    }
    setExpandedAccounts(ids);
  }, [rows, hideAdminTech, emailFilter, registrarFilter, hideRedacted, focusedEmail]);

  const collapseAll = useCallback(() => setExpandedAccounts(new Set()), []);

  const toggleRegistrar = (id: string) => {
    setRegistrarFilter((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  return (
    <div className="flex flex-col h-[calc(100vh-4rem)]">
      <div className="px-6 py-4 border-b border-gray-200 bg-surface">
        <div className="flex items-center justify-between mb-3">
          <div>
            <div className="flex flex-wrap items-center gap-3">
              <h1 className="text-2xl font-bold text-gray-900">Mind Map</h1>
              {tabs}
            </div>
            <p className="text-sm text-gray-500">
              WHOIS contact emails connected to registrars and domains.
              {!loading && ` ${emailCount} email${emailCount === 1 ? '' : 's'} · ${rows.length} domains with WHOIS${redactedCount > 0 ? ` · ${redactedCount} redacted` : ''}.`}
            </p>
            {focusedEmail && (
              <p className="text-xs text-violet-700 mt-1">
                Focused: <span className="font-mono">{focusedEmail === NO_EMAIL_KEY ? 'No public WHOIS email' : focusedEmail}</span>
                <button onClick={() => setFocusedEmail(null)} className="ml-2 text-violet-600 hover:underline">clear</button>
              </p>
            )}
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={exportPng}
              disabled={exporting || loading || nodes.length === 0}
              title="Download the current graph as a PNG"
              className="inline-flex items-center gap-2 px-3 py-2 text-sm font-medium text-gray-700 bg-surface border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50"
            >
              <Download className="w-4 h-4" />
              {exporting ? 'Exporting…' : 'Export PNG'}
            </button>
            <button
              onClick={load}
              className="inline-flex items-center gap-2 px-3 py-2 text-sm font-medium text-gray-700 bg-surface border border-gray-300 rounded-md hover:bg-gray-50"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </button>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-3">
          <div className="relative flex-1 min-w-[220px] max-w-md">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              value={emailFilter}
              onChange={(e) => setEmailFilter(e.target.value)}
              placeholder="Filter emails…"
              className="w-full pl-8 pr-3 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500"
            />
          </div>

          <button
            onClick={() => setHideAdminTech((v) => !v)}
            className={`inline-flex items-center gap-2 px-3 py-1.5 text-sm rounded-md border ${
              hideAdminTech
                ? 'bg-blue-50 border-blue-300 text-blue-700'
                : 'bg-surface border-gray-300 text-gray-700'
            }`}
          >
            {hideAdminTech ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
            {hideAdminTech ? 'Registrant only' : 'All contact roles'}
          </button>

          <button
            onClick={() => setHideRedacted((v) => !v)}
            title="Hide the bucket of domains with no public WHOIS email"
            className={`inline-flex items-center gap-2 px-3 py-1.5 text-sm rounded-md border ${
              hideRedacted
                ? 'bg-slate-100 border-slate-400 text-slate-700'
                : 'bg-surface border-gray-300 text-gray-700'
            }`}
          >
            {hideRedacted ? '🔒 Redacted hidden' : '🔒 Show redacted'}
          </button>

          <div className="flex items-center gap-1">
            <button
              onClick={expandAll}
              className="px-2.5 py-1.5 text-xs font-medium text-gray-700 bg-surface border border-gray-300 rounded-md hover:bg-gray-50"
            >
              Expand all
            </button>
            <button
              onClick={collapseAll}
              className="px-2.5 py-1.5 text-xs font-medium text-gray-700 bg-surface border border-gray-300 rounded-md hover:bg-gray-50"
            >
              Collapse all
            </button>
          </div>

          {registrars.length > 0 && (
            <div className="flex flex-wrap items-center gap-1.5">
              <span className="text-xs text-gray-500 mr-1">Registrars:</span>
              {registrars.map((r) => {
                const active = registrarFilter.has(r.id);
                return (
                  <button
                    key={r.id}
                    onClick={() => toggleRegistrar(r.id)}
                    className={`px-2 py-0.5 text-xs rounded-full border ${
                      active
                        ? 'bg-brand-600 text-white border-brand-600'
                        : 'bg-surface text-gray-700 border-gray-300 hover:bg-gray-50'
                    }`}
                  >
                    {r.name}
                  </button>
                );
              })}
              {registrarFilter.size > 0 && (
                <button
                  onClick={() => setRegistrarFilter(new Set())}
                  className="text-xs text-blue-600 hover:underline ml-1"
                >
                  clear
                </button>
              )}
            </div>
          )}
        </div>

        <div className="flex items-center flex-wrap gap-x-4 gap-y-1 mt-2 text-xs text-gray-500">
          <span className="inline-flex items-center gap-1">
            <span className="w-3 h-3 rounded-full bg-violet-200 border-2 border-violet-600"></span>
            Email <span className="text-gray-400">(hover to focus, click to pin)</span>
          </span>
          <span className="inline-flex items-center gap-1">
            <span className="w-3 h-3 rounded-full bg-slate-100 border border-dashed border-slate-400"></span>
            No public email
          </span>
          <span className="inline-flex items-center gap-1">
            <span className="w-3 h-3 border-[3px] border-gray-500" style={{ background: 'linear-gradient(135deg, #dbeafe, #fce7f3, #dcfce7)' }}></span>
            Registrar <span className="text-gray-400">(funnel for accounts)</span>
          </span>
          <span className="inline-flex items-center gap-1">
            <span className="w-3 h-3 bg-surface border border-gray-400" style={{ borderLeft: '4px solid #475569' }}></span>
            Account <span className="text-gray-400">(click to expand domains)</span>
          </span>
          <span className="inline-flex items-center gap-1">
            <span className="w-3 h-3 bg-gray-50 border border-gray-300" style={{ borderLeft: '3px solid #475569' }}></span>
            Domain <span className="text-gray-400">(R/A/T = registrant/admin/tech)</span>
          </span>
          <span className="inline-flex items-center gap-1">
            <span className="w-3 h-3 rounded-full bg-red-100 border-2 border-red-600"></span>
            <span className="text-red-600 font-medium">⚠️ Exposed</span>
            <span className="text-gray-400">(contact details match your WHOIS keywords)</span>
          </span>
        </div>
      </div>

      <div ref={flowWrapperRef} className="flex-1 relative bg-gray-50">
        {error && (
          <div className="absolute top-4 left-1/2 -translate-x-1/2 z-10 px-4 py-2 bg-red-50 border border-red-200 text-red-700 text-sm rounded-md">
            {error}
          </div>
        )}
        {!loading && nodes.length > 0 && (
          <div className="absolute top-4 left-4 z-10 bg-surface/95 backdrop-blur border border-gray-200 rounded-lg shadow-sm px-4 py-3 text-xs">
            <div className="grid grid-cols-2 gap-x-5 gap-y-1.5">
              <div>
                <div className="text-gray-400">Emails</div>
                <div className="text-lg font-semibold text-gray-900 leading-tight">{emailCount.toLocaleString()}</div>
              </div>
              <div>
                <div className="text-gray-400">Domains</div>
                <div className="text-lg font-semibold text-gray-900 leading-tight">{stats.total.toLocaleString()}</div>
              </div>
            </div>
            <div className="mt-2 pt-2 border-t border-gray-100 space-y-1">
              <div className="flex items-center justify-between gap-4">
                <span className="text-gray-500">Exposed</span>
                <span className="font-medium text-red-600">
                  {stats.exposed} {stats.total > 0 && <span className="text-gray-400">({Math.round((stats.exposed / stats.total) * 100)}%)</span>}
                </span>
              </div>
              <div className="flex items-center justify-between gap-4">
                <span className="text-gray-500">Expiring &lt;30d</span>
                <span className="font-medium text-amber-600">{stats.expiringSoon}</span>
              </div>
              <div className="flex items-center justify-between gap-4">
                <span className="text-gray-500">Liveness</span>
                <span className="inline-flex items-center gap-2">
                  <span className="inline-flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-green-500" />{stats.liveness.live}</span>
                  <span className="inline-flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-amber-500" />{stats.liveness.parked}</span>
                  <span className="inline-flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-red-500" />{stats.liveness.offline}</span>
                </span>
              </div>
              {stats.topRegistrar && (
                <div className="flex items-center justify-between gap-4">
                  <span className="text-gray-500">Top registrar</span>
                  <span className="font-medium text-gray-700 truncate max-w-[120px]" title={stats.topRegistrar[0]}>{stats.topRegistrar[0]} ({stats.topRegistrar[1]})</span>
                </div>
              )}
            </div>
          </div>
        )}
        {loading ? (
          <div className="h-full p-6"><Skeleton className="h-full w-full rounded-lg" /></div>
        ) : nodes.length === 0 ? (
          <div className="flex items-center justify-center h-full text-gray-500 text-center px-6">
            <div>
              <p className="mb-2">No WHOIS contact emails to graph.</p>
              <p className="text-sm">
                Try adjusting filters, or{' '}
                <button onClick={() => navigate('/whois')} className="text-blue-600 hover:underline">
                  populate WHOIS data
                </button>{' '}
                first.
              </p>
            </div>
          </div>
        ) : (
          <ReactFlow
            nodes={nodes}
            edges={edges}
            nodeTypes={nodeTypes}
            onNodeClick={onNodeClick}
            onNodeMouseEnter={onNodeMouseEnter}
            onNodeMouseLeave={onNodeMouseLeave}
            fitView
            minZoom={0.1}
            nodesDraggable={false}
            nodesConnectable={false}
            elementsSelectable={true}
            proOptions={{ hideAttribution: true }}
            colorMode={isDark ? 'dark' : 'light'}
          >
            <Background gap={16} size={1.5} color={isDark ? '#273246' : '#cbd5e1'} />
            <Controls />
            <MiniMap
              zoomable
              pannable
              nodeColor={(n) => {
                const data = n.data as { kind?: string; is_redacted?: boolean; is_exposed?: boolean; registrar_type?: string };
                if (data.kind === 'email') return data.is_redacted ? '#cbd5e1' : data.is_exposed ? '#dc2626' : '#a78bfa';
                if (data.kind === 'registrar' && data.registrar_type) return colorFor(data.registrar_type).border;
                if (data.kind === 'account' && data.registrar_type) return colorFor(data.registrar_type).edge;
                if (data.kind === 'domain') return data.is_exposed ? '#dc2626' : (data.registrar_type ? colorFor(data.registrar_type).edge : '#94a3b8');
                return '#94a3b8';
              }}
            />
          </ReactFlow>
        )}
      </div>
    </div>
  );
}

// The treemap views load ECharts on demand, so the Network view doesn't pay for it.
const OwnershipExplorer = lazy(() => import('../components/OwnershipExplorer'));

type MindMapView = 'registrar' | 'nominee' | 'network';
const VIEWS: { key: MindMapView; label: string; hint: string }[] = [
  { key: 'registrar', label: 'By registrar', hint: 'Registrar → account → nominee → domains' },
  { key: 'nominee', label: 'By nominee', hint: 'Nominee (WHOIS registrant) → registrar → account → domains' },
  { key: 'network', label: 'Network', hint: 'WHOIS contact emails linked to registrars and domains' },
];

/**
 * Mind Map: "who holds what". By registrar and By nominee are zoomable
 * treemaps (ECharts); Network is the original contact-email graph. The view is
 * kept in the URL (?view=) and remembered per browser.
 */
export default function MindMapPage() {
  const [params, setParams] = useSearchParams();
  const fromUrl = params.get('view') as MindMapView | null;
  const [view, setView] = useState<MindMapView>(() => {
    if (fromUrl && VIEWS.some((v) => v.key === fromUrl)) return fromUrl;
    try { const v = localStorage.getItem('mindMapView') as MindMapView | null; return v && VIEWS.some((x) => x.key === v) ? v : 'registrar'; } catch { return 'registrar'; }
  });
  const choose = (v: MindMapView) => {
    setView(v);
    try { localStorage.setItem('mindMapView', v); } catch { /* per-viewer convenience only */ }
    const next = new URLSearchParams(params); next.set('view', v); setParams(next, { replace: true });
  };
  const tabs = (
    <div className="inline-flex rounded-md border border-slate-200 p-0.5 text-sm" role="tablist" aria-label="Mind Map view">
      {VIEWS.map((v) => (
        <button key={v.key} type="button" role="tab" aria-selected={view === v.key} title={v.hint} onClick={() => choose(v.key)}
          className={`rounded px-3 py-1 font-medium ${view === v.key ? 'bg-slate-100 text-slate-900' : 'text-slate-500 hover:text-slate-700'}`}>
          {v.label}
        </button>
      ))}
    </div>
  );
  if (view === 'network') return <NetworkView tabs={tabs} />;
  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-3">
        <h1 className="text-2xl font-bold text-slate-900">Mind Map</h1>
        {tabs}
      </div>
      <p className="-mt-2 text-sm text-slate-500">{VIEWS.find((v) => v.key === view)!.hint}. Sized by number of domains; colours follow the registrar.</p>
      <Suspense fallback={<div className="card p-6"><Skeleton className="h-[520px] w-full" /></div>}>
        <OwnershipExplorer mode={view} />
      </Suspense>
    </div>
  );
}
